import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { i18n } from "../lib/config/i18n" // Import the configured i18n instance

export default class extends Controller {
  static targets = ["dateField", "timeSlotsContainer", "numberOfPeopleField", "guestsFields", "form", "errorMessage"]
  static values = {
    homepage: Boolean,
    immutableGroup: Boolean
  }
  
  // Get translations for the controller
  get translations() {
    if (typeof i18n !== 'undefined' && typeof i18n.t === 'function') {
      // i18n is available and its t method is a function.
      // i18n-js will return a "missing translation" message by default if a key is not found.
      return {
        selectTime: i18n.t('booking_controller.select_time'),
        errorLoading: i18n.t('booking_controller.error_loading'),
        selectTimeSlot: i18n.t('booking_controller.select_time_slot'),
        noSlotsAvailable: i18n.t('booking_controller.no_slots_available'),
        dateRequired: i18n.t('booking_controller.date_required'),
        timeSlotRequired: i18n.t('booking_controller.time_slot_required')
      };
    } else {
      // i18n object or i18n.t function is not available.
      // Fallback to returning string keys. This makes it clear if the i18n system failed to initialize.
      // console.warn("[BookingController] I18n object or I18n.t function not found. Using string keys as fallback.");
      return {
        selectTime: 'booking_controller.select_time',
        errorLoading: 'booking_controller.error_loading',
        selectTimeSlot: 'booking_controller.select_time_slot',
        noSlotsAvailable: 'booking_controller.no_slots_available',
        dateRequired: 'booking_controller.date_required',
        timeSlotRequired: 'booking_controller.time_slot_required'
      };
    }
  }

  connect() {
    // Set up event handlers for existing guest remove buttons
    this.setupExistingGuestRemoveButtons()
    // Initialize the number of people field with the current count
    this.updateNumberOfPeopleField()
  }

  setupExistingGuestRemoveButtons() {
    if (!this.hasGuestsFieldsTarget) return
    
    const existingRemoveButtons = this.guestsFieldsTarget.querySelectorAll('.remove-guest-btn')
    existingRemoveButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        const guestForm = e.target.closest('.guest-fields')
        this.removeGuest(guestForm)
      })
    })
  }

  handleDateSelected(event) {
    const { date, bookingId, currentTimeSlotId } = event.detail
    this.fetchTimeSlotsForDate(date, bookingId || '', currentTimeSlotId || '')
  }
  
  fetchTimeSlotsForDate(date, bookingId, currentTimeSlotId) {
    // Create URL with parameters, values are already normalized at the call site
    const homepageParam = this.homepageValue ? '&homepage=true' : '';
    const url = `/bookings/fetch_time_slots?date=${encodeURIComponent(date)}&booking_id=${encodeURIComponent(bookingId)}&current_time_slot_id=${encodeURIComponent(currentTimeSlotId)}${homepageParam}`;
    
    // Check if we're in a modal context by looking for the booking_wizard_frame
    const isInModal = document.getElementById('booking_wizard_frame') !== null;
    
    if (isInModal) {
      // Use JSON for modal context since Turbo Streams don't work well in Turbo Frames
      fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.json()
      })
      .then(data => {
        this.updateTimeSlotDropdown(data.time_slots)
      })
      .catch(error => {
        console.error("Error fetching time slots:", error)
        if (this.hasTimeSlotsContainerTarget) {
          this.timeSlotsContainerTarget.innerHTML = `
            <label class="block text-sm font-medium text-gray-700 mb-2">${this.translations.selectTime}</label>
            <p class="mt-1 text-sm text-gray-500">${this.translations.errorLoading}</p>
          `
        }
      })
    } else {
      // Use Turbo Streams for regular pages
      fetch(url, {
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.text()
      })
      .then(html => {
        // This is a Turbo Stream response
        Turbo.renderStreamMessage(html)
      })
      .catch(error => {
        console.error("Error fetching time slots:", error)
        if (this.hasTimeSlotsContainerTarget) {
          this.timeSlotsContainerTarget.innerHTML = `
            <label class="block text-sm font-medium text-gray-700 mb-2">${this.translations.selectTime}</label>
            <p class="mt-1 text-sm text-gray-500">${this.translations.errorLoading}</p>
          `
        }
      })
    }
  }
  
  updateTimeSlotDropdown(timeSlots) {
    // This function is used as a fallback if Turbo Streams don't work, or for modal context
    if (!this.hasTimeSlotsContainerTarget) return
    
    const container = this.timeSlotsContainerTarget
    
    // Clear the container
    container.innerHTML = ''
    
    // Create label
    const label = document.createElement('label')
    label.setAttribute('for', 'booking_time_slot_id')
    label.className = 'block mb-2 text-sm font-medium text-gray-700'
    label.textContent = this.translations.selectTime
    container.appendChild(label)
    
    if (timeSlots && timeSlots.length > 0) {
      // Create select dropdown
      const dropdown = document.createElement('select')
      dropdown.id = 'booking_time_slot_id'
      dropdown.name = 'booking[time_slot_id]'
      dropdown.className = 'w-full px-4 py-3 text-lg border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
      
      // Add a prompt option
      const promptOption = document.createElement('option')
      promptOption.value = ''
      promptOption.textContent = this.translations.selectTimeSlot
      dropdown.appendChild(promptOption)
      
      // Add options for each time slot
      timeSlots.forEach(slot => {
        const option = document.createElement('option')
        option.value = slot.id
        option.textContent = slot.formatted_time_range
        
        // Mark policy-restricted slots that managers may deliberately override.
        if (slot.manager_override_required) {
          option.dataset.managerOverrideRequired = 'true'
          option.classList.add('text-amber-800')
          option.textContent += ` (${slot.manager_override_label}: ${slot.unavailable_reason})`
        } else if (slot.hasOwnProperty('available') && !slot.available) {
          option.disabled = true
          option.classList.add('text-gray-400')

          if (slot.unavailable_reason) {
            option.textContent += ` (${slot.unavailable_reason})`
          }
        }
        
        if (slot.selected) {
          option.selected = true
        }
        dropdown.appendChild(option)
      })
      
      container.appendChild(dropdown)
      
      // Add help text
      const helpText = document.createElement('p')
      helpText.className = 'mt-1 text-sm text-gray-500'
      helpText.textContent = this.translations.selectTimeSlot
      container.appendChild(helpText)
    } else {
      // If no time slots available
      const noSlotsMessage = document.createElement('p')
      noSlotsMessage.className = 'mt-1 text-sm text-gray-500'
      noSlotsMessage.textContent = this.translations.noSlotsAvailable
      container.appendChild(noSlotsMessage)
    }
  }
  
  updateGuestForms() {
    // This method is no longer needed since we're using manual add/remove buttons
    // Keep it for backward compatibility but make it a no-op
  }

  addGuest() {
    if (!this.hasGuestsFieldsTarget) return
    
    // Find the next available index by looking at all existing form indices
    const nextIndex = this.getNextAvailableIndex()
    
    this.addGuestForm(nextIndex)
    this.updateNumberOfPeopleField()
  }

  getNextAvailableIndex() {
    // Get all guest forms (including hidden ones) and find the highest index used
    const allForms = this.guestsFieldsTarget.querySelectorAll('.guest-fields')
    let maxIndex = -1
    
    allForms.forEach(form => {
      // Look for any input with a name containing an index pattern like [0], [1], etc.
      const inputs = form.querySelectorAll('input[name*="guests_attributes"]')
      inputs.forEach(input => {
        const match = input.name.match(/\[(\d+)\]/)
        if (match) {
          const index = parseInt(match[1])
          if (index > maxIndex) {
            maxIndex = index
          }
        }
      })
    })
    
    return maxIndex + 1
  }

  removeGuest(guestForm) {
    const destroyField = guestForm.querySelector('.destroy-field')
    const idField = guestForm.querySelector('input[name*="[id]"]')
    
    if (destroyField && idField && idField.value) {
      // This is a persisted record, mark for destruction
      destroyField.value = "1"
      guestForm.style.display = "none"
    } else {
      // This is a new record, just remove it
      guestForm.remove()
    }
    
    this.updateNumberOfPeopleField()
  }

  updateNumberOfPeopleField() {
    if (this.immutableGroupValue || !this.hasNumberOfPeopleFieldTarget) return
    
    const visibleForms = this.guestsFieldsTarget.querySelectorAll('.guest-fields:not([style*="display: none"])')
    this.numberOfPeopleFieldTarget.value = visibleForms.length
    
    // Renumber the visible guest forms
    visibleForms.forEach((form, index) => {
      const heading = form.querySelector('h3')
      if (heading) {
        // Update the heading to show the correct guest number
        heading.textContent = heading.textContent.replace(/\d+/, index + 1)
      }
    })
  }
  
  addGuestForm(index) {
    const template = document.getElementById('guest_template')
    if (!template) return
    
    // Clone the template content
    const content = template.content.cloneNode(true)
    const newForm = content.querySelector('.guest-fields')
    if (!newForm) return
    
    // Replace the index placeholder in all attributes and text
    this.replaceAllPlaceholders(newForm, '__INDEX__', index)
    
    // Update the guest number in the heading
    const heading = newForm.querySelector('h3')
    if (heading) {
      heading.textContent = heading.textContent.replace(/\d+/, index + 1)
    }
    
    // Add event handler for remove button
    const removeBtn = newForm.querySelector('.remove-guest-btn')
    if (removeBtn) {
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault()
        const guestForm = e.target.closest('.guest-fields')
        this.removeGuest(guestForm)
      })
    }
    
    // Append the new form
    this.guestsFieldsTarget.appendChild(newForm)
  }
  
  replaceAllPlaceholders(element, placeholder, replacement) {
    // Replace placeholders in all attributes and text content
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
      null,
      false
    )
    
    let node
    while ((node = walker.nextNode()) !== null) {
      // For text nodes, replace in the text content
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = node.textContent.replace(
          new RegExp(placeholder, 'g'),
          replacement
        )
      }
      
      // For element nodes, replace in all attributes
      if (node.nodeType === Node.ELEMENT_NODE) {
        Array.from(node.attributes).forEach(attr => {
          if (attr.value.includes(placeholder)) {
            attr.value = attr.value.replace(
              new RegExp(placeholder, 'g'),
              replacement
            )
          }
        })
      }
    }
  }
  
  validateForm(event) {
    event.preventDefault()
    
    const date = this.dateFieldTarget.value
    const timeSlot = this.element.querySelector('#booking_time_slot_id').value
    
    // Reset error states
    this.clearErrors()
    
    let isValid = true
    
    if (!date) {
      this.showError(this.dateFieldTarget, this.translations.dateRequired)
      isValid = false
    }
    
    if (!timeSlot) {
      this.showError(this.element.querySelector('#booking_time_slot_id'), this.translations.timeSlotRequired)
      isValid = false
    }
    
    if (isValid) {
      // We need to submit the form programmatically because we prevented the default submit
      const form = event.target
      const submitButton = form.querySelector('input[type="submit"]')
      if (submitButton) submitButton.disabled = true
      
      // Submit the form
      form.submit()
    }
  }
  
  showError(field, message) {
    field.classList.add('border-red-500', 'focus:ring-red-500', 'focus:border-red-500')
    
    const errorDiv = document.createElement('p')
    errorDiv.className = 'mt-1 text-sm text-red-600'
    errorDiv.textContent = message
    field.parentElement.appendChild(errorDiv)
  }
  
  clearErrors() {
    // Remove error classes from fields
    const fields = [this.dateFieldTarget, this.element.querySelector('#booking_time_slot_id')]
    fields.forEach(field => {
      field.classList.remove('border-red-500', 'focus:ring-red-500', 'focus:border-red-500')
      
      // Remove error messages
      const errorMessage = field.parentElement.querySelector('.text-red-600')
      if (errorMessage) {
        errorMessage.remove()
      }
    })
  }
}